/* SKALAEN — en verdi velges ved å trykke på den, ikke ved å skrive den.
 *
 * HVORFOR IKKE ET TASTATUR
 *   Dette føres på en gangbane, i vær, ofte med hansker, på en telefon. Et
 *   tallfelt der åpner et tastatur som dekker halve skjermen og ber om en
 *   presisjon ingen har. Ett trykk gir det samme tallet på en brøkdel av
 *   tiden.
 *
 * HVORFOR IKKE BARE FIRE KLASSER
 *   Det var det nærliggende forslaget, og det taper to ting. Protokollen sier
 *   at miljømålinger «registreres som kontinuerlige verdier» (§3), så en klasse
 *   kaster bort tallet skjemaet er til for. Og for oksygen er klassen ikke
 *   engang mulig å velge alene: grensen ER en funksjon av temperaturen —
 *   DOmaxFI = 2,95·T + 21,15 — så «grønn» er 56 % ved 5 °C og 94 % ved 18 °C.
 *   Å be noen velge et bånd de ikke kan se kantene på er verre enn å be dem
 *   skrive et tall.
 *
 *   Så: bånd som BAKGRUNN og verdi som valg. Man trykker på tallet, og fargen
 *   er der man havnet. Skjemaet lærer bort protokollen ved å bli brukt.
 *
 * BÅNDENE KOMMER FRA TJENESTEN
 *   Aldri hardkodet her. En kopi i nettleseren er nettopp slik en trykket verdi
 *   og en skåret verdi ender opp uenige, og for oksygen er de ikke konstanter
 *   i det hele tatt.
 */

import { useEffect, useRef, useState } from 'react'

export interface Band {
  from: number
  to: number
  /** 0 grønn, 1 gul, 2 oransje, 3 rød. */
  score: number
}

export interface Merke {
  at: number
  label: string
}

export function Skala({
  verdi, onVerdi, min, max, steg, enhet, bands, merker, tittel, hint, sperret,
}: {
  verdi: number | null
  onVerdi: (v: number | null) => void
  min: number
  max: number
  steg: number
  enhet: string
  /** Tom eller utelatt: sporet tegnes uten farge. Det er det ærlige svaret for
   *  saltholdighet, som protokollen ikke skårer, og for oksygen før noen har
   *  ført temperaturen. */
  bands?: Band[] | null
  merker?: Merke[]
  tittel: string
  /** Sagt under sporet: hvor et forhåndsutfylt tall kom fra. */
  hint?: string | null
  /** Grunn til at skalaen ikke kan brukes ennå, i ord. */
  sperret?: string | null
}) {
  const spor = useRef<HTMLDivElement>(null)
  const [drar, setDrar] = useState(false)

  const pst = (v: number) => ((v - min) / (max - min)) * 100

  const rund = (v: number) => {
    const n = Math.round(v / steg) * steg
    // Steget kan være 0,1 og flyttall er flyttall: uten dette blir 14,5 til
    // 14,500000000000002 i tekstfeltet ved siden av.
    const d = (String(steg).split('.')[1] ?? '').length
    return Number(Math.min(max, Math.max(min, n)).toFixed(d))
  }

  const fraX = (clientX: number) => {
    const r = spor.current?.getBoundingClientRect()
    if (!r || r.width === 0) return
    onVerdi(rund(min + ((clientX - r.left) / r.width) * (max - min)))
  }

  /* Ingen avhengighetsliste: lytterne byttes ut ved hver render, slik at de
     alltid holder gjeldende `onVerdi` og gjeldende grenser. Bare mens noen
     drar. */
  useEffect(() => {
    if (!drar) return
    const move = (e: PointerEvent) => fraX(e.clientX)
    const up = () => setDrar(false)
    window.addEventListener('pointermove', move)
    window.addEventListener('pointerup', up)
    return () => {
      window.removeEventListener('pointermove', move)
      window.removeEventListener('pointerup', up)
    }
  })

  /* Kantene mellom båndene, som tall under sporet. Dette er hele forskjellen
     mellom en fargestripe og en skala: uten dem ser man at man er i det grønne
     og ikke hvor det grønne slutter. For oksygen flytter de seg med
     temperaturen, som er nettopp det som gjør dem verdt å tegne. */
  const kanter = (bands ?? [])
    .map((b) => b.from)
    .concat((bands ?? []).length ? [bands![bands!.length - 1]!.to] : [])
    .filter((v, i, a) => a.indexOf(v) === i)
    .filter((v) => v > min && v < max)

  const nudge = (retning: number) =>
    onVerdi(rund((verdi ?? (min + max) / 2) + retning * steg))

  const desimaler = (String(steg).split('.')[1] ?? '').length

  return (
    <div className={`skala${sperret ? ' er-sperret' : ''}`}>
      <div className="skala-hode">
        <span className="skala-tittel">{tittel}</span>
        <span className="skala-verdi">
          {verdi === null
            ? <span className="skala-tom">ikke ført</span>
            : <>{verdi.toFixed(desimaler).replace('.', ',')} {enhet}</>}
        </span>
      </div>

      {sperret ? (
        <p className="skala-sperret">{sperret}</p>
      ) : (
        <>
          <div className="skala-rad">
            <button type="button" className="skala-knapp" aria-label="Lavere"
                    onClick={() => nudge(-1)}>−</button>

            <div ref={spor} className="skala-spor"
                 role="slider" tabIndex={0}
                 aria-label={tittel}
                 aria-valuemin={min} aria-valuemax={max}
                 aria-valuenow={verdi ?? undefined}
                 aria-valuetext={verdi === null ? 'ikke ført'
                   : `${verdi} ${enhet}`}
                 onPointerDown={(e) => { setDrar(true); fraX(e.clientX) }}
                 onKeyDown={(e) => {
                   if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') {
                     e.preventDefault(); nudge(-1)
                   }
                   if (e.key === 'ArrowRight' || e.key === 'ArrowUp') {
                     e.preventDefault(); nudge(1)
                   }
                 }}>
              {/* Båndene som en BÅND nederst, ikke som fyll i hele sporet.
                  Fylt i full høyde og dempet nok til å skrive oppå ble alle
                  fire til samme olivenbrune grøt over det mørkegrønne — det er
                  ikke fargene som ikke kommuniserer, det er gjennomsiktigheten.
                  Som en tynn stripe kan de stå i sin egen farge. */}
              {(bands ?? []).map((b, i) => (
                <span key={i} className={`skala-band band-${b.score}`}
                      style={{ left: `${pst(b.from)}%`,
                               width: `${pst(b.to) - pst(b.from)}%` }} />
              ))}
              {kanter.map((v, i) => (
                <span key={`k${i}`} className="skala-kant"
                      style={{ left: `${pst(v)}%` }} />
              ))}
              {(merker ?? []).map((m, i) => (
                <span key={i} className="skala-merke"
                      style={{ left: `${pst(m.at)}%` }}
                      data-merke={m.label} />
              ))}
              {verdi !== null && (
                <span className="skala-tommel" style={{ left: `${pst(verdi)}%` }} />
              )}
            </div>

            <button type="button" className="skala-knapp" aria-label="Høyere"
                    onClick={() => nudge(1)}>+</button>
          </div>

          {/* Tallene står DER kanten er, ikke i en rad under. En skala med
              «0» til venstre og «22» til høyre sier ingenting om hvor det
              grønne begynner; en 10-er rett under overgangen sier alt. */}
          <div className="skala-akse">
            <span className="skala-ende" style={{ left: '0%' }}>{min}</span>
            {kanter.map((v, i) => (
              <span key={i} className="skala-tick" style={{ left: `${pst(v)}%` }}>
                {String(v).replace('.', ',')}
              </span>
            ))}
            <span className="skala-ende er-hoyre" style={{ left: '100%' }}>{max}</span>
          </div>

          <div className="skala-under">
            {hint && <span className="skala-hint">{hint}</span>}
            {verdi !== null && (
              <button type="button" className="skala-blank"
                      onClick={() => onVerdi(null)}>Tøm</button>
            )}
          </div>
        </>
      )}
    </div>
  )
}
